import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions } from '@config/components';
import { useConfigContext } from '@config/context';
import type { RoleCatalogueEntry } from '@config/interfaces';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import type { RoleRow } from '../interfaces';
import { roleColumns, rolePermissionColumns } from '../tableConfig';

export const RolesList = (): ReactElement => {
  const { t } = useTranslation();
  const { configuration, roleCatalogue } = useConfigContext();

  const dataSource: RoleRow[] = useMemo(() => {
    const entries: RoleCatalogueEntry[] =
      roleCatalogue ??
      Object.entries(configuration?.groupPermissions ?? {}).map(([role, resources]) => ({
        role,
        permissions: Object.entries(resources).flatMap(([resource, actions]) => actions.map(action => `${resource}:${action}`)),
      }));

    return entries
      .map(entry => ({
        role: entry.role,
        group: entry.group,
        description: entry.description,
        deprecated: entry.deprecated,
        count: entry.permissions.length,
        permissions: [...entry.permissions]
          .sort((a, b) => a.localeCompare(b))
          .map(permission => {
            const [resource, action = ''] = permission.split(':');
            return { permission, resource, action };
          }),
      }))
      .sort((a, b) => a.role.localeCompare(b.role));
  }, [configuration, roleCatalogue]);

  return (
    <Table
      id="configRoles"
      rowKey="role"
      dataSource={dataSource}
      columns={roleColumns}
      expanded={{ rowKey: 'permission', columnsKey: 'permissions', columns: rolePermissionColumns }}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t('config.sections.roles')}
    >
      <ConfigTableActions />
    </Table>
  );
};
